import { Fragment } from 'react';
import type { ChecklistAccount, ChecklistEntry, ChecklistResponse } from '../../lib/checklist';
import { buildRows, groupRows } from '../../lib/checklistGrouping';
import { LeafMark } from '../LeafMark';
import { formatDayColumn } from '../../lib/period';
import { TaskControl } from './TaskControl';

function findEntry(response: ChecklistResponse | undefined, characterTaskId: number): ChecklistEntry | null {
  if (!response) {
    return null;
  }
  for (const account of response.accounts) {
    for (const entry of account.tasks) {
      if (entry.characterTaskId === characterTaskId) {
        return entry;
      }
    }
    for (const character of account.characters) {
      for (const entry of character.tasks) {
        if (entry.characterTaskId === characterTaskId) {
          return entry;
        }
      }
    }
  }
  return null;
}

function findAccount(response: ChecklistResponse | undefined, accountId: number): ChecklistAccount | null {
  return response?.accounts.find((a) => a.accountId === accountId) ?? null;
}

interface DayColumn {
  iso: string;
  response: ChecklistResponse | undefined;
  isPending: boolean;
  isFuture: boolean;
}

interface WeekGridProps {
  accountId: number | null;
  days: DayColumn[];
  todayIso: string;
  taskCategoryBySlug: Map<string, string>;
  onToggleDaily: (entry: ChecklistEntry, dateIso: string, count: number) => void;
  onToggleShared: (entry: ChecklistEntry, count: number) => void;
}

/**
 * The dense spreadsheet-style grid: rows = enabled tasks grouped by category, columns = the 7 GMS
 * week days. Wrapped in its own horizontally-scrolling container with a sticky first column, so it
 * never causes the page body to scroll sideways on narrow screens.
 */
export function WeekGrid({
  accountId,
  days,
  todayIso,
  taskCategoryBySlug,
  onToggleDaily,
  onToggleShared,
}: WeekGridProps) {
  if (accountId === null) {
    return <p className="text-sm text-text-muted">No account selected yet.</p>;
  }

  // The canonical row shape comes from the most recent loaded day — attachments don't usually
  // change mid-week, but this keeps the grid showing the fullest available task list.
  const canonicalResponse = [...days].reverse().find((d) => d.response)?.response;
  const canonicalAccount = findAccount(canonicalResponse, accountId);

  if (!canonicalAccount) {
    return (
      <p className="text-sm text-text-muted">This week hasn’t started yet — nothing to show.</p>
    );
  }

  const groups = groupRows(buildRows(canonicalAccount, taskCategoryBySlug));

  if (groups.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-border-strong bg-bg-elevated p-6 text-center">
        <LeafMark className="mx-auto text-text-faint" size={24} />
        <p className="mt-3 font-display text-sm font-semibold text-text">Nothing tracked yet</p>
        <p className="mt-2 text-sm text-text-muted">
          Head to <span className="text-accent">Manage tasks</span> and turn on the dailies and
          weeklies you want to track.
        </p>
      </section>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-bg-elevated">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-bg-elevated-hover">
            <th className="sticky left-0 z-10 min-w-[11rem] bg-bg-elevated-hover px-3 py-2 text-left font-display text-xs font-semibold uppercase tracking-wider text-text-faint">
              Task
            </th>
            {days.map((day) => (
              <th
                key={day.iso}
                className={[
                  'min-w-[4.5rem] px-2 py-2 text-center font-display text-xs font-semibold uppercase tracking-wider',
                  day.iso === todayIso ? 'text-accent' : 'text-text-faint',
                ].join(' ')}
              >
                {formatDayColumn(day.iso)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={group.category}>
              <tr className="bg-bg-elevated-hover">
                <td
                  colSpan={days.length + 1}
                  className="sticky left-0 bg-bg-elevated-hover px-3 py-1.5 font-display text-xs font-semibold uppercase tracking-widest text-text-faint"
                >
                  {group.label}
                </td>
              </tr>
              {group.rows.map((row) => (
                <tr
                  key={row.entry.characterTaskId}
                  className="border-b border-border last:border-0 hover:bg-bg-elevated-hover"
                >
                  <td className="sticky left-0 z-10 min-w-[11rem] bg-bg-elevated px-3 py-2 align-middle">
                    <p className="truncate text-sm font-medium text-text">{row.entry.name}</p>
                    <p className="truncate text-xs text-text-faint">{row.ownerLabel}</p>
                  </td>
                  {row.entry.cadence === 'DAILY' ? (
                    days.map((day) => {
                      if (day.isFuture) {
                        return (
                          <td key={day.iso} className="px-2 py-2 text-center text-text-faint">
                            —
                          </td>
                        );
                      }
                      if (day.isPending) {
                        return (
                          <td key={day.iso} className="px-2 py-2 text-center text-text-faint">
                            …
                          </td>
                        );
                      }
                      const dayEntry = findEntry(day.response, row.entry.characterTaskId);
                      return (
                        <td key={day.iso} className="px-2 py-2 text-center">
                          {dayEntry ? (
                            <TaskControl
                              count={dayEntry.count}
                              maxCount={dayEntry.maxCount}
                              label={`${row.entry.name} on ${day.iso}`}
                              onChange={(count) => onToggleDaily(dayEntry, day.iso, count)}
                            />
                          ) : (
                            <span className="text-text-faint">—</span>
                          )}
                        </td>
                      );
                    })
                  ) : (
                    <td colSpan={days.length} className="px-2 py-2 text-center">
                      <TaskControl
                        count={row.entry.count}
                        maxCount={row.entry.maxCount}
                        label={`${row.entry.name} (${row.entry.cadence.toLowerCase()})`}
                        onChange={(count) => onToggleShared(row.entry, count)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
